import { z } from 'zod'

/** What a custom field holds, as the API names `CustomFieldType`. */
export const customFieldTypes = ['text', 'number', 'checkbox', 'dropdown', 'checkboxList', 'person'] as const

export type CustomFieldType = (typeof customFieldTypes)[number]

const dependencySchema = z.object({ fieldId: z.string(), value: z.string() })

const listValueSchema = z.object({
  value: z.string(),
  dependencies: z.array(dependencySchema).nullish().transform((value) => value ?? []),
})

/**
 * One custom field as an owner's API sends it (GW-29's `CustomFieldDefinition`), its id being
 * the owner's public id for it. A dropdown's list value with dependencies is offered only while
 * one of them holds.
 */
export const customFieldDefinitionSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullish(),
  tooltip: z.string().nullish(),
  type: z.enum(customFieldTypes),
  required: z.boolean().default(false),
  unique: z.boolean().default(false),
  maxLength: z.number().int().nullish(),
  listValues: z.array(listValueSchema).nullish().transform((value) => value ?? []),
  displayGroup: z.string().nullish(),
  order: z.number().int().default(0),
  enabled: z.boolean().default(true),
})

export type CustomFieldDefinition = z.output<typeof customFieldDefinitionSchema>

/**
 * A record's value for one custom field, as the API carries it: only the property for the
 * field's type is set. A person is a User Profile's public id.
 */
export const customFieldValueSchema = z.object({
  fieldId: z.string(),
  text: z.string().nullish(),
  number: z.number().int().nullish(),
  checked: z.boolean().nullish(),
  choices: z.array(z.string()).nullish(),
  personId: z.string().nullish(),
})

export type CustomFieldValue = z.output<typeof customFieldValueSchema>

/**
 * A field's value as a form holds it: text for text, number and dropdown fields, `true` or
 * `false` for a checkbox, the chosen values of a checkbox list, and a person's id or `null`.
 */
export type CustomFieldFormValue = string | boolean | readonly string[] | null

/** Every field's form value, by field id. */
export type CustomFieldFormValues = Readonly<Record<string, CustomFieldFormValue>>

/** The longest text any field holds, as the API's `CustomFieldRules.TextLimit`. */
export const textLimit = 2000
